import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useCopy } from "../../context/CopyContext";
import { useAppContext } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import Header from "../Header";
import Button from "../Button";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";
import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import ProgressiveBlur from "react-progressive-blur";

const FacialTrackingView: React.FC = React.memo(() => {
  const navigate = useNavigate();
  const { getCopy } = useCopy();
  const { state } = useAppContext();
  const { brandConfig } = useBrand();

  // State
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [selectedAreas, setSelectedAreas] = useState<string[]>([]);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [isWebcamRunning, setIsWebcamRunning] = useState(false);
  const [faceLandmarker, setFaceLandmarker] = useState<FaceLandmarker | null>(
    null
  );
  const [showLandmarkNumbers, setShowLandmarkNumbers] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [isAutoProgressing, setIsAutoProgressing] = useState(false);  
  const [isScanning, setIsScanning] = useState(false);
  const [scanCancelCounter, setScanCancelCounter] = useState<number>(0);
  const [isFaceOutOfBounds, setIsFaceOutOfBounds] = useState<boolean>(false);
  const [scanningTextIndex, setScanningTextIndex] = useState<number>(0);

  // Debug scanning state changes
  useEffect(() => {
    console.log("Scanning state changed:", isScanning);
    if (isScanning) {
      console.log("SCANNING UI SHOULD NOW BE VISIBLE");
    }
  }, [isScanning]);

  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  // Removed lastFrameTimeRef - no longer needed without frame rate limiting
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastLandmarksRef = useRef<any[] | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressAnimationRef = useRef<number | null>(null);
  const scanAnimationRef = useRef<number | null>(null);
  const pauseStateRef = useRef<number | null>(null);
  const memoryPurgeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const textCycleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const scanTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const frameCountRef = useRef<number>(0);
  const scanPausedRef = useRef<boolean>(false);
  const lastDetectRef = useRef<number>(0);

  // Add refs for interpolation
  const lastDetectedLandmarksRef = useRef<any[] | null>(null);
  const previousDetectedLandmarksRef = useRef<any[] | null>(null);
  const lastDetectionTimeRef = useRef<number>(0);

  // Add a ref to track if we need to redraw
  const needsRedrawRef = useRef<boolean>(true);

  // Cache getBoundingClientRect calls for performance
  const videoRectRef = useRef<DOMRect | null>(null);
  const canvasRectRef = useRef<DOMRect | null>(null);

  // Canvas context state management
  const canvasContextStateRef = useRef<{
    fillStyle: string;
    strokeStyle: string;
    lineWidth: number;
    font: string;
  } | null>(null);

  // Constants
  const CANVAS_WIDTH = 1080;
  const CANVAS_HEIGHT = 1920;
  const TRACKING_WIDTH = 1080;
  const TRACKING_HEIGHT = 1920;
  // Removed frame rate optimizations to prevent flickering
  const COUNTDOWN_DURATION = 3; // 3 seconds countdown
  const SCAN_DURATION = 10; // 10 seconds scanning
  const MEMORY_PURGE_INTERVAL = 30000; // Purge memory every 30 seconds
  const DETECT_INTERVAL_MS = 1000 / 20;

  // Scanning text messages that cycle during analysis
  const scanningTexts = [
    "Starting scan...",
    "Analyzing skin...",
    "Processing skin analysis data...",
    "Matching products to your skin type...",    
    "Creating personalized recommendations...",    
    "Generating your exclusive offer...",
  ];

  // Update rects only when needed (resize, etc.)
  const updateRects = useCallback(() => {
    if (videoRef.current && canvasRef.current) {
      videoRectRef.current = videoRef.current.getBoundingClientRect();
      canvasRectRef.current = canvasRef.current.getBoundingClientRect();
    }
  }, []);

  // Start text cycling during scanning
  const startTextCycling = useCallback(() => {
    setScanningTextIndex(0);
    
    const cycleText = () => {
      setScanningTextIndex(prev => {
        const nextIndex = prev + 1;
        // Stop at the last message instead of looping
        if (nextIndex >= scanningTexts.length) {
          // Clear the interval when we reach the last message
          if (textCycleTimerRef.current) {
            clearInterval(textCycleTimerRef.current);
            textCycleTimerRef.current = null;
          }
          return prev; // Stay on the last message
        }
        return nextIndex;
      });
    };
    
    // Cycle every 2 seconds
    textCycleTimerRef.current = setInterval(cycleText, 2000);
  }, [scanningTexts.length]);

  // Stop text cycling
  const stopTextCycling = useCallback(() => {
    if (textCycleTimerRef.current) {
      clearInterval(textCycleTimerRef.current);
      textCycleTimerRef.current = null;
    }
    setScanningTextIndex(0);
  }, []);

  // Batch canvas operations for better performance
  const batchCanvasOperations = useCallback((
    ctx: CanvasRenderingContext2D,
    operations: (() => void)[]
  ) => {
    ctx.beginPath();
    operations.forEach(op => op());
    ctx.fill();
    ctx.stroke();
  }, []);

  // Countdown management functions (memoized for performance)
  const clearCountdown = useCallback(() => {
    if (countdownTimerRef.current) {
      clearTimeout(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (progressAnimationRef.current) {
      cancelAnimationFrame(progressAnimationRef.current);
      progressAnimationRef.current = null;
    }
      if (scanAnimationRef.current) {
        cancelAnimationFrame(scanAnimationRef.current);
        scanAnimationRef.current = null;
      }
      if (pauseStateRef.current) {
        cancelAnimationFrame(pauseStateRef.current);
        pauseStateRef.current = null;
      }
      if (memoryPurgeTimerRef.current) {
        clearTimeout(memoryPurgeTimerRef.current);
        memoryPurgeTimerRef.current = null;
      }
      setCountdown(null);
      setIsAutoProgressing(false);      
      setIsScanning(false);
      
      // Stop text cycling
      stopTextCycling();
  }, [stopTextCycling]);  

  // Add refs for scan timeout management with pause handling
  const scanStartTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const pauseStartTimeRef = useRef<number>(0);

  // Scanning animation function - simplified with proper pause handling
  const animateScan = useCallback((startTime: number) => {
    console.log("Starting scan animation...");
    
    // Store the start time for pause/resume calculations
    scanStartTimeRef.current = startTime;
    pausedTimeRef.current = 0;
    pauseStartTimeRef.current = 0;
    
    // Set up completion handler
    const handleAnimationEnd = () => {
      console.log("Scanning complete, navigating to OfferView");
      // Stop text cycling
      stopTextCycling();
      // Navigate to offer view
      navigate("/offer");
    };

    // Start the initial timeout
    const startTimeout = () => {
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
      }
      scanTimeoutRef.current = setTimeout(() => {
        console.log("Scan duration completed, navigating to OfferView");
        handleAnimationEnd();
      }, SCAN_DURATION * 1000);
    };

    startTimeout();

    // Also try to listen for CSS animation end as backup
    const progressBar = document.querySelector('.scan-progress-bar') as HTMLElement;
    const progressText = document.querySelector('.scan-progress-text') as HTMLElement;
    
    if (progressBar) {
      console.log("Progress bar found, adding animation end listener as backup");
      progressBar.addEventListener('animationend', () => {
        console.log("CSS animation ended, clearing timeout and navigating");
        if (scanTimeoutRef.current) {
          clearTimeout(scanTimeoutRef.current);
          scanTimeoutRef.current = null;
        }
        handleAnimationEnd();
      }, { once: true });
    }

    // Update progress text during animation (minimal DOM updates)
    const updateProgressText = () => {
      if (progressText && isScanning) {
        const now = Date.now();
        const elapsed = now - scanStartTimeRef.current - pausedTimeRef.current;
        const progress = Math.min(100, Math.round((elapsed / (SCAN_DURATION * 1000)) * 100));
        progressText.textContent = `${progress}%`;
        
        if (progress < 100) {
          scanAnimationRef.current = requestAnimationFrame(updateProgressText);
        }
      }
    };

    // Start progress text updates
    scanAnimationRef.current = requestAnimationFrame(updateProgressText);
  }, [navigate, isScanning, stopTextCycling, SCAN_DURATION]);

  // Memory management function to prevent slowdown over time
  const purgeMemory = useCallback(() => {
    console.log("Purging memory to prevent slowdown...");
    
    // Clear landmark cache
    lastLandmarksRef.current = null;
    
    // Force garbage collection if available (Chrome DevTools)
    if (window.gc) {
      window.gc();
    }
    
    // Clear any offscreen canvas contexts
    if (offscreenCanvasRef.current) {
      const offscreenCtx = offscreenCanvasRef.current.getContext("2d");
      if (offscreenCtx) {
        offscreenCtx.clearRect(0, 0, offscreenCanvasRef.current.width, offscreenCanvasRef.current.height);
      }
    }
    
    // Clear main canvas
    const canvas = canvasRef.current;
    if (canvas) {
      const canvasCtx = canvas.getContext("2d");
      if (canvasCtx) {
        canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }
    
    console.log("Memory purge complete");
  }, []);

  const startCountdown = useCallback(
    (areas: string[]) => {
      console.log("Starting countdown for areas:", areas);

      // Clear any existing countdown
      clearCountdown();
      
      setIsAutoProgressing(true);
      setCountdown(COUNTDOWN_DURATION);          

      // Start countdown interval
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          console.log("Countdown tick:", prev);
          if (prev === null || prev <= 1) {
            console.log(
              "Countdown interval ending - NOT calling clearCountdown()"
            );
            // Don't call clearCountdown() here - let the timeout handle the transition
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      // Set timeout for auto-progression
      countdownTimerRef.current = setTimeout(() => {        

        // Clear the interval first
        if (countdownIntervalRef.current) {
          clearInterval(countdownIntervalRef.current);
          countdownIntervalRef.current = null;
        }

        // Set scanning state
        setIsScanning(true);
        setIsFaceOutOfBounds(false);
        scanPausedRef.current = false;
        
        // Start text cycling
        startTextCycling();

        // Clear other countdown timers
        if (countdownTimerRef.current) {
          clearTimeout(countdownTimerRef.current);
          countdownTimerRef.current = null;
        }
        if (progressAnimationRef.current) {
          cancelAnimationFrame(progressAnimationRef.current);
          progressAnimationRef.current = null;
        }

        // Reset countdown states but keep scanning
        setCountdown(null);
        setIsAutoProgressing(false);        

        // Start scanning animation
        const scanStartTime = Date.now();
        animateScan(scanStartTime);
      }, COUNTDOWN_DURATION * 1000);

      // Start memory purge timer
      memoryPurgeTimerRef.current = setTimeout(() => {
        purgeMemory();
        // Restart the memory purge timer
        memoryPurgeTimerRef.current = setTimeout(() => {
          purgeMemory();
        }, MEMORY_PURGE_INTERVAL);
      }, MEMORY_PURGE_INTERVAL);
    },
    [clearCountdown, animateScan, purgeMemory, startTextCycling]
  );

  // Handlers
  const handleAreaToggle = useCallback(
    (areaId: string) => {
      setSelectedAreas((prev) => {
        let newSelectedAreas: string[];

        if (areaId === "everything") {
          // Toggle "everything" - if selected, clear all others; if not selected, select only "everything"
          newSelectedAreas = prev.includes("everything")
            ? prev.filter((id) => id !== "everything")
            : ["everything"];
        } else {
          // For specific areas, remove "everything" if it's selected and toggle the specific area
          const filteredPrev = prev.filter((id) => id !== "everything");
          newSelectedAreas = filteredPrev.includes(areaId)
            ? filteredPrev.filter((id) => id !== areaId)
            : [...filteredPrev, areaId];
        }

        // Handle countdown based on selection
        if (newSelectedAreas.length > 0) {
          startCountdown(newSelectedAreas);
        } else {
          clearCountdown();
        }

        return newSelectedAreas;
      });
    },
    [startCountdown, clearCountdown]
  );

  // Handle close scanning
  const handleCloseScanning = useCallback(() => {
    console.log("Closing scanning, clearing all timeouts and refs");
    setIsScanning(false);
    setIsFaceOutOfBounds(false);
    scanPausedRef.current = false;
    setScanCancelCounter(prev => prev + 1); // Increment counter to reset animation
    
    // Stop text cycling
    stopTextCycling();
    
    // Clear scan timeout
    if (scanTimeoutRef.current) {
      console.log("Clearing scan timeout");
      clearTimeout(scanTimeoutRef.current);
      scanTimeoutRef.current = null;
    }
    
    // Clear animation refs
    if (scanAnimationRef.current) {
      cancelAnimationFrame(scanAnimationRef.current);
      scanAnimationRef.current = null;
    }
    
    if (pauseStateRef.current) {
      cancelAnimationFrame(pauseStateRef.current);
      pauseStateRef.current = null;
    }
    
    // Reset timing refs
    scanStartTimeRef.current = 0;
    pausedTimeRef.current = 0;
    pauseStartTimeRef.current = 0;
  }, [stopTextCycling]);

  // Handle back navigation (close button in header)
  const handleBack = useCallback(() => {
    // Stop any active scanning
    handleCloseScanning();
    
    // Navigate back to skin analysis view
    navigate("/skin-analysis");
  }, [navigate, handleCloseScanning]);

  // Toggle landmark numbers for debugging
  const toggleLandmarkNumbers = useCallback(() => {
    setShowLandmarkNumbers(prev => !prev);
  }, []);

  // Face landmarker initialization
  const initializeFaceLandmarker = useCallback(async () => {
    try {
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm"
      );

      const landmarker = await FaceLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: "/shared/models/face_landmarker.task",
          delegate: "GPU", // Add this line
        },
        runningMode: "VIDEO",
        numFaces: 1,
        outputFaceBlendshapes: true,
        outputFacialTransformationMatrixes: true,
      });
      setFaceLandmarker(landmarker);
    } catch (error) {
      console.error("Error initialising face landmarker", error);
      setCameraError(
        "Failed to initialise face landmarker. Please refresh the page and try again."
      );
    }
  }, []);

  // Facial area configuration - using brand tertiary color
  const facialAreas = useMemo(
    () => {
      const tertiaryColor = brandConfig?.colors?.tertiary?.light || "#FFFFFF";
      
      return {
        // Complete face outline using MediaPipe's face oval landmarks
        faceOutline: {
          points: [
            10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365,
            379, 378, 400, 377, 152, 148, 176, 149, 150, 136, 172, 58, 132, 93,
            234, 127, 162, 21, 54, 103, 67, 109,
          ],
          color: tertiaryColor,
          fillColor: `${tertiaryColor}26`, // 15% opacity
          strokeColor: tertiaryColor,
          strokeWidth: 2,
        },
        forehead: {
          points: [
            10, 109, 67, 103, 54, 21, 162, 139, 70, 63, 105, 66, 107, 9, 336, 296,
            334, 293, 300, 368, 389, 251, 284, 332, 297, 338,
          ],
          color: tertiaryColor,
          fillColor: `${tertiaryColor}26`, // 15% opacity
          strokeColor: tertiaryColor,
          strokeWidth: 2,
        },
        leftCheek: {
          points: [
            123, 147, 187, 205, 36, 142, 100, 120, 230, 229, 228, 31, 111, 116,
          ],
          color: tertiaryColor,
          fillColor: `${tertiaryColor}26`, // 15% opacity
          strokeColor: tertiaryColor,
          strokeWidth: 2,
        },
        rightCheek: {
          points: [
            352, 376, 411, 425, 266, 371, 329, 349, 450, 449, 448, 340, 345,
          ],
          color: tertiaryColor,
          fillColor: `${tertiaryColor}26`, // 15% opacity
          strokeColor: tertiaryColor,
          strokeWidth: 2,
        },
        nose: {
          points: [
            9, 107, 55, 128, 47, 142, 49, 115, 220, 45, 4, 275, 440, 371, 277, 343, 465, 285, 336, 9
          ],
          color: tertiaryColor,
          fillColor: `${tertiaryColor}26`, // 15% opacity
          strokeColor: tertiaryColor,
          strokeWidth: 2,
        },
      };
    },
    [brandConfig]
  );

  // Map normalized (0–1) video coords to canvas pixels, mirroring object-cover
  const mapPoint = useCallback(
    (
      nx: number,
      ny: number,
      canvas: HTMLCanvasElement,
      video: HTMLVideoElement
    ): { x: number; y: number } => {
      const vw = video.videoWidth || 1;
      const vh = video.videoHeight || 1;
      
      // Use cached rects for performance
      const videoRect = videoRectRef.current;
      const canvasRect = canvasRectRef.current;
      
      // Fallback to getBoundingClientRect if cache is empty
      if (!videoRect || !canvasRect) {
        updateRects();
        return mapPoint(nx, ny, canvas, video); // Retry with updated cache
      }
      
      // Calculate the scale factor for object-cover
      const scaleX = canvasRect.width / vw;
      const scaleY = canvasRect.height / vh;
      const scale = Math.max(scaleX, scaleY);
      
      // Calculate the offset to center the video
      const offsetX = (canvasRect.width - vw * scale) / 2;
      const offsetY = (canvasRect.height - vh * scale) / 2;
      
      // Map the normalized coordinates to canvas pixel coordinates
      const canvasX = offsetX + nx * vw * scale;
      const canvasY = offsetY + ny * vh * scale;
      
      // Scale to canvas internal dimensions
      const finalX = (canvasX / canvasRect.width) * canvas.width;
      const finalY = (canvasY / canvasRect.height) * canvas.height;

      return {
        x: finalX,
        y: finalY,
      };
    },
    [updateRects]
  );

  // Helper function to draw landmark numbers (memoized)
  const drawLandmarkNumbers = useCallback(
    (
      landmarks: any[],
      canvasCtx: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      video: HTMLVideoElement
    ) => {
      if (!showLandmarkNumbers || landmarks.length === 0) return;

      // Save context state
      canvasCtx.save();
      
      // Set up clean styling for numbers only
      canvasCtx.fillStyle = "#FFFFFF";
      canvasCtx.strokeStyle = "#000000";
      canvasCtx.lineWidth = 2;
      canvasCtx.font = "12px Arial";

      // Draw small circles and numbers for each landmark
      for (let i = 0; i < landmarks.length; i++) {
        const landmark = landmarks[i];
        const { x, y } = mapPoint(landmark.x, landmark.y, canvas, video);

        // Draw small white circle with black border
        canvasCtx.beginPath();
        canvasCtx.arc(x, y, 3, 0, 2 * Math.PI);
        canvasCtx.fill();
        canvasCtx.stroke();

        // Draw the index number
        canvasCtx.fillText(i.toString(), x + 6, y - 6);
      }

      // Restore context state
      canvasCtx.restore();
    },
    [showLandmarkNumbers, mapPoint]
  );

  // Helper function to highlight a facial area (standardized styling)
  const highlightFacialArea = useCallback(
    (
      landmarks: any[],
      pointIndices: number[],
      canvasCtx: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      video: HTMLVideoElement,
      config: {
        color: string;
        fillColor: string;
        strokeColor: string;
        strokeWidth: number;
      }
    ) => {
      if (pointIndices.length === 0) return;

      // Get all valid points (optimized)
      const validPoints: { x: number; y: number }[] = [];
      for (let i = 0; i < pointIndices.length; i++) {
        const landmark = landmarks[pointIndices[i]];
        if (landmark) {
          const { x, y } = mapPoint(landmark.x, landmark.y, canvas, video);
          validPoints.push({ x, y });
        }
      }

      if (validPoints.length < 3) return; // Need at least 3 points for a polygon

      // Save context state
      canvasCtx.save();

      // Draw filled polygon with smooth outline
      canvasCtx.fillStyle = config.fillColor;
      canvasCtx.strokeStyle = config.strokeColor;
      canvasCtx.lineWidth = config.strokeWidth;
      canvasCtx.lineCap = "round";
      canvasCtx.lineJoin = "round";
      canvasCtx.beginPath();

      // Start the path
      canvasCtx.moveTo(validPoints[0].x, validPoints[0].y);

      // Create smooth curves between points
      for (let i = 1; i < validPoints.length; i++) {
        const currentPoint = validPoints[i];
        const nextPoint = validPoints[(i + 1) % validPoints.length];

        // Calculate control point for smooth curve
        const controlPointX = (currentPoint.x + nextPoint.x) * 0.5;
        const controlPointY = (currentPoint.y + nextPoint.y) * 0.5;

        // Use quadratic curve for smooth transitions
        canvasCtx.quadraticCurveTo(
          currentPoint.x,
          currentPoint.y,
          controlPointX,
          controlPointY
        );
      }

      // Close the path smoothly
      const firstPoint = validPoints[0];
      const lastPoint = validPoints[validPoints.length - 1];

      const controlPointX = (lastPoint.x + firstPoint.x) * 0.5;
      const controlPointY = (lastPoint.y + firstPoint.y) * 0.5;

      canvasCtx.quadraticCurveTo(
        lastPoint.x,
        lastPoint.y,
        controlPointX,
        controlPointY
      );

      // Close the path, fill, and stroke
      canvasCtx.closePath();
      canvasCtx.fill();
      canvasCtx.stroke();

      // Restore context state
      canvasCtx.restore();
    },
    [mapPoint]
  );

  // Pre-calculate tracking bounds (optimized)
  const trackingBounds = useMemo(() => {
    const centerX = CANVAS_WIDTH / 2;
    const centerY = CANVAS_HEIGHT / 2;
    return {
      left: centerX - TRACKING_WIDTH / 2,
      right: centerX + TRACKING_WIDTH / 2,
      top: centerY - TRACKING_HEIGHT / 2,
      bottom: centerY + TRACKING_HEIGHT / 2,
      // Normalized bounds for direct comparison with landmark coordinates (0-1)
      leftNorm: (centerX - TRACKING_WIDTH / 2) / CANVAS_WIDTH,
      rightNorm: (centerX + TRACKING_WIDTH / 2) / CANVAS_WIDTH,
      topNorm: (centerY - TRACKING_HEIGHT / 2) / CANVAS_HEIGHT,
      bottomNorm: (centerY + TRACKING_HEIGHT / 2) / CANVAS_HEIGHT,
    };
  }, []);

  // Optimized helper function to check if face is within tracking bounds
  const isFaceInTrackingBounds = useCallback(
    (landmarks: any[]) => {
      if (landmarks.length === 0) return false;

      // Use normalized coordinates to avoid multiplication
      const { leftNorm, rightNorm, topNorm, bottomNorm } = trackingBounds;
      
      const keyPoints = [234, 10, 454, 152];

      for (let i = 0; i < keyPoints.length; i++) {
        const landmark = landmarks[keyPoints[i]];
        if (landmark) {
          const x = landmark.x;
          const y = landmark.y;

          if (
            x >= leftNorm &&
            x <= rightNorm &&
            y >= topNorm &&
            y <= bottomNorm
          ) {
            return true;
          }
        }
      }

      return false;
    },
    [trackingBounds]
  );

  // Helper function to highlight all selected areas
  const highlightSelectedAreas = useCallback(
    (
      landmarks: any[],
      canvasCtx: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      video: HTMLVideoElement
    ) => {
      // Only show highlights if user has selected specific areas
      if (selectedAreas.length === 0) {
        return;
      }

      // Map UI area IDs to facial area configurations
      const areaMapping: { [key: string]: string[] } = {
        everything: ["faceOutline"], // Show complete face outline for "everything"
        forehead: ["forehead"],
        cheeks: ["leftCheek", "rightCheek"],
        nose: ["nose"],
      };

      selectedAreas.forEach((selectedArea) => {
        const areasToHighlight = areaMapping[selectedArea] || [selectedArea];

        areasToHighlight.forEach((areaName) => {
          const config = facialAreas[areaName as keyof typeof facialAreas];
          if (config) {
            highlightFacialArea(
              landmarks,
              config.points,
              canvasCtx,
              canvas,
              video,
              {
                color: config.color,
                fillColor: config.fillColor,
                strokeColor: config.strokeColor,
                strokeWidth: config.strokeWidth,
              }
            );
          }
        });
      });
    },
    [selectedAreas, highlightFacialArea, facialAreas]
  );

  // Helper function to check if landmarks are equal (within tolerance)
  const areLandmarksEqual = useCallback((prev: any[], current: any[]) => {
    if (!prev || !current || prev.length !== current.length) return false;
    
    const tolerance = 0.001; // Small tolerance for floating point comparison
    
    for (let i = 0; i < prev.length; i++) {
      const prevLandmark = prev[i];
      const currentLandmark = current[i];
      
      if (
        Math.abs(prevLandmark.x - currentLandmark.x) > tolerance ||
        Math.abs(prevLandmark.y - currentLandmark.y) > tolerance ||
        Math.abs(prevLandmark.z - currentLandmark.z) > tolerance
      ) {
        return false;
      }
    }
    
    return true;
  }, []);

  // Helper function to interpolate between two landmark sets
  const interpolateLandmarks = useCallback((prev: any[], current: any[], factor: number) => {
    if (!prev || !current || prev.length !== current.length) return current;
    
    return current.map((landmark, index) => ({
      x: prev[index].x + (landmark.x - prev[index].x) * factor,
      y: prev[index].y + (landmark.y - prev[index].y) * factor,
      z: prev[index].z + (landmark.z - prev[index].z) * factor,
    }));
  }, []);

  // Face detection and drawing with performance optimizations
  const predictWebcam = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const now = performance.now();

    if (!video || !canvas || !faceLandmarker || !isWebcamRunning) return;

    const canvasCtx = canvas.getContext("2d");
    if (!canvasCtx) return;

    // Skip if video isn't ready
    if (video.currentTime === 0 && video.readyState < 3) {
      animationFrameRef.current = requestAnimationFrame(predictWebcam);
      return;
    }

    // Only run detection every 50ms (20fps)
    let shouldDetect = false;
    if (now - lastDetectRef.current >= DETECT_INTERVAL_MS) {
      shouldDetect = true;
      lastDetectRef.current = now;
    }

    // Run face detection only when needed
    if (shouldDetect) {
      try {
        const results = faceLandmarker.detectForVideo(video, performance.now());
        
        if (results?.faceLandmarks?.length > 0) {
          // Store previous landmarks for interpolation
          previousDetectedLandmarksRef.current = lastDetectedLandmarksRef.current;
          lastDetectedLandmarksRef.current = results.faceLandmarks[0];
          lastDetectionTimeRef.current = now;
          needsRedrawRef.current = true; // Add this line
        } else {
          lastDetectedLandmarksRef.current = null;
          previousDetectedLandmarksRef.current = null;
          needsRedrawRef.current = true; // Add this line
        }

        // Handle scanning logic here...
        if (isScanning) {
          if (results?.faceLandmarks?.length > 0) {
            let faceInBounds = false;
            for (const landmarks of results.faceLandmarks) {
              if (isFaceInTrackingBounds(landmarks)) {
                faceInBounds = true;
                // Resume scanning if it was paused
                if (scanPausedRef.current) {
                  console.log("Face returned to bounds, resuming scan");
                  scanPausedRef.current = false;
                  setIsFaceOutOfBounds(false);
                  
                  // Resume the timeout with remaining time
                  const now = Date.now();
                  if (pauseStartTimeRef.current > 0) {
                    const pauseDuration = now - pauseStartTimeRef.current;
                    pausedTimeRef.current += pauseDuration;
                    console.log("Was paused for:", pauseDuration, "ms, total paused:", pausedTimeRef.current, "ms");
                    pauseStartTimeRef.current = 0;
                  }
                  
                  // Calculate remaining time and restart timeout
                  const elapsed = now - scanStartTimeRef.current - pausedTimeRef.current;
                  const remainingTime = Math.max(0, (SCAN_DURATION * 1000) - elapsed);
                  
                  if (remainingTime > 0) {
                    console.log("Resuming scan, elapsed:", elapsed, "ms, remaining:", remainingTime, "ms");
                    scanTimeoutRef.current = setTimeout(() => {
                      console.log("Scan duration completed (resumed), navigating to OfferView");
                      stopTextCycling();
                      navigate("/offer");
                    }, remainingTime);
                  } else {
                    console.log("No time remaining, completing scan immediately");
                    stopTextCycling();
                    navigate("/offer");
                  }
                }
                break;
              }
            }
            
            // Pause scanning if face is out of bounds
            if (!faceInBounds && !scanPausedRef.current) {
              console.log("Face out of bounds, pausing scan");
              scanPausedRef.current = true;
              setIsFaceOutOfBounds(true);
              
              // Track when pausing started
              pauseStartTimeRef.current = Date.now();
              
              // Clear the timeout
              if (scanTimeoutRef.current) {
                console.log("Clearing timeout due to pause");
                clearTimeout(scanTimeoutRef.current);
                scanTimeoutRef.current = null;
              }
            }
          } else {
            // No face detected, pause scanning
            if (!scanPausedRef.current) {
              console.log("No face detected, pausing scan");
              scanPausedRef.current = true;
              setIsFaceOutOfBounds(true);
              
              // Track when pausing started
              pauseStartTimeRef.current = Date.now();
              
              // Clear the timeout
              if (scanTimeoutRef.current) {
                console.log("Clearing timeout due to pause");
                clearTimeout(scanTimeoutRef.current);
                scanTimeoutRef.current = null;
              }
            }
          }
        }
      } catch (error) {
        console.error("Error during face detection", error);
      }
    }

    // Always draw using interpolated landmarks (60fps)
    if (lastDetectedLandmarksRef.current && selectedAreas.length > 0) {
      let landmarksToDraw = lastDetectedLandmarksRef.current;
      
      // Only interpolate if landmarks have actually changed
      if (previousDetectedLandmarksRef.current && lastDetectedLandmarksRef.current) {
        const landmarksChanged = !areLandmarksEqual(
          previousDetectedLandmarksRef.current, 
          lastDetectedLandmarksRef.current
        );
        
        if (landmarksChanged) {
          const timeSinceLastDetection = now - lastDetectionTimeRef.current;
          const interpolationFactor = Math.min(timeSinceLastDetection / DETECT_INTERVAL_MS, 1);
          
          landmarksToDraw = interpolateLandmarks(
            previousDetectedLandmarksRef.current,
            lastDetectedLandmarksRef.current,
            interpolationFactor
          );
        }
        // If landmarks haven't changed, use current landmarks directly (no interpolation needed)
      }
      
      if (isFaceInTrackingBounds(landmarksToDraw)) {
        if (needsRedrawRef.current) {
          canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
          needsRedrawRef.current = false;
        }

        // Show landmark numbers if debug mode is on
        if (showLandmarkNumbers) {
          drawLandmarkNumbers(landmarksToDraw, canvasCtx, canvas, video);
        }

        // Highlight selected facial areas
        highlightSelectedAreas(landmarksToDraw, canvasCtx, canvas, video);
      }
    } else if (selectedAreas.length === 0) {
      // Clear canvas when no areas are selected
      canvasCtx.clearRect(0, 0, canvas.width, canvas.height);
    }

    // Continue the animation loop
    if (isWebcamRunning) {
      animationFrameRef.current = requestAnimationFrame(predictWebcam);
    }
  }, [
    faceLandmarker,
    isWebcamRunning,
    showLandmarkNumbers,
    selectedAreas,
    drawLandmarkNumbers,
    highlightSelectedAreas,
    isFaceInTrackingBounds,
    isScanning,
    DETECT_INTERVAL_MS,
    interpolateLandmarks,
    areLandmarksEqual,
    stopTextCycling,
    navigate,
    SCAN_DURATION,
  ]);  


  // Camera setup
  useEffect(() => {
    // Capture refs to avoid stale closure warnings
    const video = videoRef.current;
    const canvas = canvasRef.current;

    const startCamera = async () => {
      try {
        if (state.settings.demoMode) {
          // Demo mode: use video file instead of webcam
          if (videoRef.current) {
            videoRef.current.src = "/webcam-feed-demo.mp4";
            videoRef.current.loop = true;
            videoRef.current.muted = true;
            videoRef.current.onloadedmetadata = () => {
              const canvas = canvasRef.current;
              if (canvas) {
                // Set canvas to 1080x1920 for demo mode
                canvas.width = 1080;
                canvas.height = 1920;
                canvas.style.width = `1080px`;
                canvas.style.height = `1920px`;
              }
              setIsCameraActive(true);
              // Update rects after video is loaded
              updateRects();
              // Start playing the video
              videoRef.current?.play();
            };
          }
        } else {
          // Real camera mode
          if (!navigator.mediaDevices?.getUserMedia) {
            throw new Error("Camera access not supported");
          }

          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: "user",
              width: { ideal: 720 },
              height: { ideal: 1280 },
            },
          });

          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.onloadedmetadata = () => {
              const canvas = canvasRef.current;
              if (canvas) {
                canvas.width = CANVAS_WIDTH;
                canvas.height = CANVAS_HEIGHT;
                canvas.style.width = `${CANVAS_WIDTH}px`;
                canvas.style.height = `${CANVAS_HEIGHT}px`;
              }
              setIsCameraActive(true);
              // Update rects after video is loaded
              updateRects();
            };
          }
        }
      } catch (error) {
        console.error("Error accessing camera:", error);
        setCameraError(
          "Unable to access camera. Please check permissions and try again."
        );
      }
    };

    startCamera();
    initializeFaceLandmarker();

    return () => {
      // Clean up video stream
      if (video?.srcObject) {
        const stream = video.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
        video.srcObject = null;
      }

      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }

      // Clear canvas
      if (canvas) {
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }

      // Reset states and clear caches
      setIsWebcamRunning(false);
      setIsCameraActive(false);
      // Removed lastFrameTimeRef reset - no longer needed
      lastLandmarksRef.current = null;
      frameCountRef.current = 0;

      lastDetectedLandmarksRef.current = null;
      previousDetectedLandmarksRef.current = null;
      needsRedrawRef.current = true;
      
      // Clear rect caches
      videoRectRef.current = null;
      canvasRectRef.current = null;
      
      // Clear canvas context state
      canvasContextStateRef.current = null;
      
      // Clear scan timeout management refs
      scanStartTimeRef.current = 0;
      pausedTimeRef.current = 0;
      pauseStartTimeRef.current = 0;

      // Clean up offscreen canvas if it exists
      if (offscreenCanvasRef.current) {
        const offscreenCtx = offscreenCanvasRef.current.getContext("2d");
        if (offscreenCtx) {
          offscreenCtx.clearRect(
            0,
            0,
            offscreenCanvasRef.current.width,
            offscreenCanvasRef.current.height
          );
        }
        offscreenCanvasRef.current = null;
      }

      // Clean up countdown timers
      if (countdownTimerRef.current) {
        clearTimeout(countdownTimerRef.current);
        countdownTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (progressAnimationRef.current) {
        cancelAnimationFrame(progressAnimationRef.current);
        progressAnimationRef.current = null;
      }
      if (scanAnimationRef.current) {
        cancelAnimationFrame(scanAnimationRef.current);
        scanAnimationRef.current = null;
      }
      if (pauseStateRef.current) {
        cancelAnimationFrame(pauseStateRef.current);
        pauseStateRef.current = null;
      }
      if (memoryPurgeTimerRef.current) {
        clearTimeout(memoryPurgeTimerRef.current);
        memoryPurgeTimerRef.current = null;
      }
      if (textCycleTimerRef.current) {
        clearInterval(textCycleTimerRef.current);
        textCycleTimerRef.current = null;
      }
      if (scanTimeoutRef.current) {
        clearTimeout(scanTimeoutRef.current);
        scanTimeoutRef.current = null;
      }
    };
  }, [initializeFaceLandmarker, state.settings.demoMode, updateRects]);

  // Auto-start prediction when ready
  useEffect(() => {
    if (isCameraActive && faceLandmarker && !isWebcamRunning) {
      setIsWebcamRunning(true);
    }
  }, [isCameraActive, faceLandmarker, isWebcamRunning]);

  // Start prediction loop
  useEffect(() => {
    if (isWebcamRunning && faceLandmarker) {
      predictWebcam();
    }
  }, [isWebcamRunning, faceLandmarker, predictWebcam]);

  // Area options configuration
  const areaOptions = [
    { id: "everything", label: getCopy("facialTracking.options.everything") },
    { id: "forehead", label: getCopy("facialTracking.options.forehead") },
    { id: "cheeks", label: getCopy("facialTracking.options.cheeks") },
    { id: "nose", label: getCopy("facialTracking.options.nose") },
  ].map((option) => ({
    ...option,
    isSelected: selectedAreas.includes(option.id),
  }));

  return (
    <>
      <style>
        {`
          @keyframes scanProgress {
            from { width: 0%; }
            to { width: 100%; }
          }
          
          .scan-progress-bar {
            will-change: width;
            transform: translateZ(0);
          }
        `}
      </style>
      <div className="bg-brand-background h-screen w-screen relative overflow-hidden">
      {/* Camera Feed Background */}
      <div className="absolute inset-0">
        {cameraError ? (
          <div className="flex items-center justify-center h-full bg-gray-900">
            <div className="text-center text-white">
              <p className="text-xl mb-4">{cameraError}</p>
              <Button
                variant="secondary"
                onClick={() => window.location.reload()}
              >
                Retry Camera Access
              </Button>
            </div>
          </div>
        ) : (
          <div className="relative w-full h-full flex justify-center">
            
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="object-cover object-center"
            />
            <canvas
              ref={canvasRef}
              className="absolute object-cover object-center"
              style={{ 
                zIndex: 1,
                willChange: 'transform'
              }}
            />
          </div>
        )}
      </div>

      {/* Progressive Blur Overlay */}
      <ProgressiveBlur
        className="absolute inset-x-0 bottom-0 h-[400px]"
        intensity={80}
      />
      {/* Dark overlay on top of blur */}
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-b from-transparent via-transparent to-black/60 h-[600px]" />

      {/* Content Container */}
      <div className="absolute inset-0 z-10 flex flex-col">
        {/* Header */}
        <div className="flex justify-center mx-20 mt-20">
          <div className="w-full max-w-[920px]">
            <Header
              showItems={false}
              showSettings={!isScanning}
              showEmailAddress={false}
              showClose={true}
              onClose={handleBack}
            />
          </div>
        </div>

        {/* Debug Controls */}
        {!isScanning && (
          <div className="absolute top-4 right-4 z-20">
            <button
              onClick={toggleLandmarkNumbers}
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                showLandmarkNumbers
                  ? 'bg-white text-black'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              {showLandmarkNumbers ? 'Hide' : 'Show'} Landmarks
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col justify-end">          
          {isScanning ? (
            <>
              <motion.div
                key={`scan-animation-${scanCancelCounter}-${isFaceOutOfBounds ? 'paused' : 'active'}`}
                className="absolute left-0 right-0 bottom-[-410px] h-[400px] box-border bg-red-500/60 z-99"
                animate={isFaceOutOfBounds ? {
                  opacity: 0,
                  y: "30vh"
                } : {
                  opacity: 1,
                  y: ["30vh", "-125vh", "-125vh", "30vh", "30vh"],
                  background: [
                    "linear-gradient(to bottom, rgba(255,255,255,.6) 0%, rgba(255,255,255,0) 100%)",
                    "linear-gradient(to bottom, rgba(255,255,255,.6) 0%, rgba(255,255,255,0) 100%)",
                    "linear-gradient(to top, rgba(255,255,255,.6) 0%, rgba(255,255,255,0) 100%)",
                    "linear-gradient(to top, rgba(255,255,255,.6) 0%, rgba(255,255,255,0) 100%)",
                    "linear-gradient(to bottom, rgba(255,255,255,.6) 0%, rgba(255,255,255,0) 100%)",
                  ],
                  borderTopWidth: ["4px", "4px", "0px", "0px", "4px"],
                  borderBottomWidth: ["0px", "0px", "4px", "4px", "0px"],
                  borderColor: [
                    "#ffffff",
                    "#ffffff",
                    "#ffffff",
                    "#ffffff",
                    "#ffffff",
                  ],
                }}
                transition={isFaceOutOfBounds ? {
                  duration: 0.3,
                  ease: "easeOut"
                } : {
                  duration: 6,
                  ease: "easeInOut",
                  repeat: Infinity,
                  repeatDelay: 0,
                  times: [0, 0.49, 0.51, 0.99, 1],
                }}
              />              
              <motion.div
                className="flex flex-col gap-8 w-full mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
              >
                {/* Scanning Text */}
                <div className="text-center">
                  {isFaceOutOfBounds ? (
                    <>
                      <h2 className="font-brand-primary font-medium text-[32px] leading-[42px] text-white tracking-[-0.32px] mb-4">
                        Please return to view
                      </h2>
                      <p className="font-brand-normal text-[24px] leading-[31px] text-white tracking-[-0.24px]">
                        Scanning paused - position your face in the frame
                      </p>
                    </>
                  ) : (
                    <>
                      <h2 className="font-brand-primary font-medium text-[32px] leading-[42px] text-white tracking-[-0.32px] mb-4">
                        Skincare analysis in progress
                      </h2>
                        <motion.p 
                          key={scanningTextIndex}
                          className="font-brand-normal text-[24px] leading-[31px] text-white tracking-[-0.24px]"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                        >
                          {scanningTexts[scanningTextIndex]}
                        </motion.p>
                    </>
                  )}
                </div>
                
                {/* Scanning Progress Bar - CSS Animation */}
                <motion.div
                  className="flex flex-col items-center gap-3 mb-12"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <div className="w-64 h-1 bg-white/20 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full scan-progress-bar"
                      style={{
                        width: '0%',
                        animation: `scanProgress ${SCAN_DURATION}s linear forwards`,
                        animationPlayState: isFaceOutOfBounds ? 'paused' : 'running'
                      }}
                    />
                  </div>                  
                </motion.div>
              </motion.div>
            </>
          ) : (
            // Selection State
            <motion.div
              className="flex flex-col gap-10 w-full mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {/* Question */}
              <div className="text-center">
                <h2 className="font-brand-primary font-medium text-[32px] leading-[42px] text-white tracking-[-0.32px]">
                  {getCopy("facialTracking.question")}
                </h2>

                {/* Countdown Indicator */}
                {isAutoProgressing && countdown !== null && (
                  <motion.div
                    className="flex flex-col items-center gap-3 mt-6"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
                  >
                    <motion.div
                      className="text-white/80 text-sm uppercase tracking-wider font-normal"
                      animate={{
                        opacity: [0.8, 1, 0.8],
                      }}
                      transition={{
                        duration: 1,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                      style={{
                        fontFamily: "var(--brand-font-button)",
                        letterSpacing: "var(--brand-button-letter-spacing)",
                      }}
                    >
                      Auto-progressing in {countdown} second
                      {countdown !== 1 ? "s" : ""}
                    </motion.div>
                    <div className="w-48 h-1 bg-white/20 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-white rounded-full"
                        initial={{ width: "100%" }}
                        animate={{ width: 0}}
                        transition={{ duration: COUNTDOWN_DURATION, ease: "linear" }}                        
                      />
                    </div>
                  </motion.div>
                )}
              </div>

              {/* Area Options */}
              <div className="relative w-full overflow-x-auto snap-x snap-center mb-10 carousel-scroll-hide">
                <div className="flex gap-10 px-20 py-4">
                  {areaOptions.map((option) => (
                    <motion.button
                      key={option.id}
                      onClick={() => handleAreaToggle(option.id)}
                      className={`flex flex-col items-center justify-center gap-6 p-8 rounded-[1000px] min-h-[88px] w-[280px] flex-shrink-0 snap-center transition-all duration-200 backdrop-blur-md ${
                        option.isSelected
                          ? "bg-white border-2 border-white/50 shadow-lg"
                          : "bg-white/40 border-2 border-white/25 hover:bg-white/60"
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <p
                        className="font-brand-normal font-brand-medium text-black text-[32px] leading-[31px] tracking-[-0.64px] uppercase"
                        style={{
                          fontFamily: "var(--brand-font-button)",
                          letterSpacing: "var(--brand-button-letter-spacing)",
                        }}
                      >
                        {option.label}
                      </p>
                    </motion.button>
                  ))}
                  <div className="w-20 flex-shrink-0" />{" "}
                  {/* Add padding at end of scroll to match left padding */}
                </div>
              </div>
            </motion.div>
          )}
        </div>

        {/* Bottom Section */}
        <div className="flex flex-col gap-16 items-center mx-20 mb-10 z-100">
          {/* Cancel Button - only show when scanning */}
          {isScanning && (
            <div className="flex justify-center">
              <Button
                variant="secondary"                
                onClick={handleCloseScanning}
                className="border-white [&_p]:!text-white"
              >
                Cancel
              </Button>
            </div>
          )}
          
          {/* Footer */}
          <div className="flex justify-center mt-5">
            <PoweredBy
              textColorOverride="white"
              logoColorOverride="dark"
              className="opacity-60"
              showProvider={false}
            />
          </div>
        </div>
      </div>

      {/* Items Modal */}
      <ItemsModalView
        isOpen={showItemsModal}
        onClose={() => setShowItemsModal(false)}
      />
      </div>
    </>
  );
});

export default FacialTrackingView;
